import type { AppDependencies } from '@/appDependencies'
import { getSearchController, LIBRARY_FILTER_TYPE } from '@/search/searchState'
import { createDisposer, noop, onLine, type Maybe, type Unsubscribe } from '@amazon/vinyl-util'
import { getAuth } from '@/auth/authRef'
import { AuthStatus } from '@amzn/bf-playback'
import { SignInWall } from '@/signInWall/SignInWall'
import { back, getRouter, type RouteOptions, type RouteViewFactory } from '@/router/router'
import { startViewContextRouterFeed } from '@/router/viewContextRouterFeed'
import { startNowPlayingViewContextFeed } from '@/blackbolt/nowPlayingViewContextFeed'
import { getDeviceConfig, isTv } from '@/device/deviceConfig'
import { el } from '@amazon/vinyl-tsx'
import { installLazily } from '@/core/installLazily'
import { ctx } from '@/util/context'
import { bindFullscreenEscapeLock } from '@/fullscreen/escapeKeyLock'
import {
    NotFoundPage,
    WithheldRoutePage,
    FeatureUnavailablePage,
    type WithheldRouteDetails,
} from '@/components/NotFoundPage'
import { downloadsSupported } from '@/downloads/nativePlayback'
import type { DownloadsView } from '@/library/DownloadsPage'
import type { ComputerView } from '@/library/ComputerLibraryPage'
import type { Pages } from '@/pages/allPages'
import { ErrorPage } from '@/error/ErrorPage'
import { DetailPageSkeleton } from '@/components/DetailPageSkeleton'
import { ACTIVITY_FEED_ROUTE } from '@/profile/activityFeedRoute'
import { PROFILE_SELECTOR_ROUTE, PROFILE_SETUP_ROUTE } from '@/profile/profileFlowRoutes'
import { BROWSE_ALL_ROUTE, HOME_ROUTE, PLAY_ROUTE } from '@/router/homeRoutes'
import {
    kidsModeGuarded,
    kidsModeRestrictedPageGuarded,
    renderKidsModeRestrictedPage,
} from '@/kidsMode/kidsModeRouteGuard'
import { merchEnabled } from '@/device/merchGate'
import { audiobooksMarketEnabled } from '@/audiobooksMarket'
import { videoOnDemandEnabled } from '@/video/videoOnDemand'
import { featureEnabled } from '@/core/featureGate'
import { openChatPane } from '@/blackbolt/chatPaneState'
import { isPodcastCategorySegment } from '@/router/podcastCategorySegment'
import { staticPage } from '@/staticRender'
import { getMarketplace } from '@/marketplace'
import { isClassicTerritory } from '@/customer/classicTerritory'
import { loadArePodcastsAvailable } from '@/customer/podcastsAvailable'
import { loadIsActivityFeedAvailable } from '@/customer/activityFeedAvailable'
import { tasteCollectionTerritory } from '@/tastes/tasteCollectionTerritory'
import { parsePreselectedArtistIds } from '@/tastes/preselectedArtists'
import { SUBSCRIPTION_MANAGEMENT_ROUTES } from '@/subscription/subscriptionManagementRoutes'

/** The smart TV's now-playing controls: the mobile full player's, docked beside the stage. */
/** A Downloads view, in the desktop app only; elsewhere the page doesn't exist. */
function downloadsPage(deps: AppDependencies, pages: Pages, view: (id: string) => DownloadsView) {
    return async (params: { readonly id?: string }) => {
        if (!downloadsSupported()) return NotFoundPage()
        const { DownloadsPage } = await pages.DownloadsPage()
        return DownloadsPage({ deps, view: view(params.id ?? '') })
    }
}

/** A Computer library view, in a desktop app that plays the computer's files; elsewhere Not Found. */
function computerPage(deps: AppDependencies, pages: Pages, view: (id: string) => ComputerView) {
    return async (params: { readonly id?: string }) => {
        const { localFilesSupported } = await pages.localFilesSupported()
        if (!(await localFilesSupported())) return NotFoundPage()
        const { ComputerLibraryPage } = await pages.ComputerLibraryPage()
        return ComputerLibraryPage({ deps, view: view(params.id ?? '') })
    }
}

async function tvNowPlayingSidePanel(deps: AppDependencies, pages: Pages): Promise<HTMLElement> {
    const [player, { FullPlayerPanel }] = await Promise.all([
        deps.playerPromise,
        pages.FullPlayerPanel(),
    ])
    return FullPlayerPanel({ deps: { ...deps, player } })
}

async function podcastStageTransport(deps: AppDependencies, pages: Pages): Promise<HTMLElement> {
    const [player, { PodcastStageTransport }] = await Promise.all([
        deps.playerPromise,
        pages.PodcastStageTransport(),
    ])
    return PodcastStageTransport({ deps: { ...deps, player } })
}

const { errorHandler } = ctx('routes')

// Routes the rewrite through the router so `currentPath` and `bullfrogDepth` stay in sync;
// `replace` leaves no back-stack entry, and the re-nav renders the target, so this returns null.
const redirectTo = (target: string) => () => {
    getRouter().navigateTo(target, { replace: true })
    return null
}

/** Wraps a merch route factory so the route resolves to Not Found where merch is off. */
function merchGuarded<T extends Record<string, string>>(
    factory: RouteViewFactory<T>
): RouteViewFactory<T> {
    return async (params, query) =>
        (await merchEnabled()) ? factory(params, query) : WithheldRoutePage()
}

/** Wraps an audiobook route factory so the route resolves to Not Found outside an audiobooks marketplace. */
function audiobooksGuarded<T extends Record<string, string>>(
    factory: RouteViewFactory<T>,
    withheld?: WithheldRouteDetails
): RouteViewFactory<T> {
    return async (params, query) =>
        (await audiobooksMarketEnabled()) ? factory(params, query) : WithheldRoutePage(withheld)
}

/** Wraps a podcast route factory so the route says podcasts are unavailable in an ROE territory other than Ireland. */
function podcastsGuarded<T extends Record<string, string>>(
    factory: RouteViewFactory<T>
): RouteViewFactory<T> {
    return async (params, query) =>
        (await loadArePodcastsAvailable())
            ? factory(params, query)
            : FeatureUnavailablePage('podcastsUnavailableInTerritory')
}

/** Wraps a video route factory so the route resolves to Not Found where video on demand is off. */
function videoOnDemandGuarded<T extends Record<string, string>>(
    factory: RouteViewFactory<T>
): RouteViewFactory<T> {
    return async (params, query) =>
        (await videoOnDemandEnabled()) ? factory(params, query) : WithheldRoutePage()
}

async function renderTasteCollection(
    pages: Pages,
    returnTo: Maybe<string>,
    preselectedArtistIds: readonly string[]
) {
    const { musicTerritory } = await getMarketplace()
    if (!tasteCollectionTerritory(musicTerritory)) return WithheldRoutePage()
    const { TastesPage } = await pages.TastesPage()
    return TastesPage({ territory: musicTerritory, redirectPath: returnTo, preselectedArtistIds })
}

/** The app's route table; `pages` loads what each route renders (the app passes `ALL_PAGES`). */
export function registerRoutes(deps: AppDependencies, pages: Pages): Unsubscribe {
    const router = getRouter()

    // Shared render helpers so `/`, `/feed`, and `/all` render the same surface
    // without duplicating the dynamic-import wiring. Chat is an overlay (mounted at
    // app root by AppView), not a route, so the feed is identical on every width.
    const renderFeed = async () => (await pages.Feed()).Feed({ deps })
    const renderBrowseHome = async ({ showLeanBack = false } = {}) => {
        const { BrowseHome } = await pages.BrowseHome()
        return BrowseHome({ deps, showLeanBack })
    }

    // `/playlists/...` and its `/user-playlists/...` alias share one factory;
    // the playlist id is namespaced server-side, so both render the same page.
    const playlistDetailRoute = (pattern: string): RouteOptions<['playlistId', 'mode']> => ({
        pattern,
        paramNames: ['playlistId', 'mode'],
        allowPreload: true,
        factory: async (props) => {
            return (await pages.PlaylistDetailPage()).PlaylistDetailPage({
                playlistId: props.playlistId,
                deps,
                editMode: props.mode === 'edit',
            })
        },
    })

    const videoSeeMoreRoute = (pattern: string): RouteOptions<['id']> => ({
        pattern,
        paramNames: ['id'],
        allowPreload: true,
        factory: videoOnDemandGuarded(async (params) => {
            const { SeeAllPage } = await pages.SeeAllPage()
            return SeeAllPage({ widgetId: params.id, deps })
        }),
    })

    const browseHomeSeeMoreRoute = (pattern: string): RouteOptions<['id']> => ({
        pattern,
        paramNames: ['id'],
        allowPreload: true,
        factory: async (params) => {
            const { SeeAllPage } = await pages.SeeAllPage()
            return SeeAllPage({ widgetId: params.id, deps })
        },
    })

    // Maestro's just-composed bag lives only in memory; a stale/shared id (no
    // prior generation this session) resolves nothing and renders not-found.
    const generatedPlaylistRoute = (pattern: string): RouteOptions<['token']> => ({
        pattern,
        paramNames: ['token'],
        factory: async (props) => {
            const { loadGeneratedBag } = await pages.maestroService()
            const bag = await loadGeneratedBag(props.token)
            if (!bag) return NotFoundPage()
            return (await pages.PlaylistDetailPage()).PlaylistDetailPage({
                playlistId: bag.id,
                deps,
                llmGenerated: bag,
            })
        },
    })

    // Before configure, whose initial navigation shows the first page; a static
    // render has no customer to report on. Metrics are best-effort, so a failed
    // chunk load is dropped.
    const stopPageViews = staticPage
        ? noop
        : router.on('pageShown', (event) => {
              const query = new URLSearchParams(location.search)
              import('@/routePageViews')
                  .then(({ reportRoutedPage }) => reportRoutedPage(event, query))
                  .catch(noop)
          })
    router.configure({
        stage: el('#stage'),
        // A signed-out session's browse/home queries 401 (anon credentials
        // aren't established in every browser), so don't eagerly warm routes
        // the user hasn't navigated to; navigation-time loads still run. Offline there is nothing
        // to warm, and every warmed view's fetch would fail.
        canPreload: () => getAuth().isSignedIn() && onLine(),
        routes: [
            {
                // Home renders the browse-all surface in place — URL stays `/`,
                // no redirect: the home button reads active only while the path
                // is `/` (see Header), so a redirect would leave it permanently
                // inactive. Same surface as `/all` for signed-in and anon.
                pattern: HOME_ROUTE,
                paramNames: [],
                allowPreload: true,
                factory: () => renderBrowseHome(),
            },
            {
                // Agent feed — the Ask-AI destination. Gated on the `amp` feature
                // flag: with the gate off the route resolves to NotFound, so the
                // page stays registered but disappears until re-enabled. Opening
                // it raises the chat sidebar over the feed, as if the input bar
                // was focused (chat is an overlay, not a route).
                pattern: '/feed',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    if (!(await featureEnabled('amp'))) return NotFoundPage()
                    if (!getAuth().isSignedIn()) return SignInWall()
                    return renderFeed()
                },
            },
            {
                pattern: '/taste-collection',
                paramNames: [],
                queryKeys: ['redirectPath'],
                allowPreload: true,
                factory: (_params, query) =>
                    renderTasteCollection(pages, query.get('redirectPath'), []),
            },
            {
                pattern: '/onboarding/tastes',
                paramNames: [],
                queryKeys: ['returnTo', 'preselectedArtistIds'],
                allowPreload: true,
                factory: (_params, query) =>
                    renderTasteCollection(
                        pages,
                        query.get('returnTo'),
                        parsePreselectedArtistIds(query.get('preselectedArtistIds'))
                    ),
            },
            { pattern: '/tastes', paramNames: [], factory: redirectTo('/taste-collection') },
            {
                pattern: '/tasteCollection',
                paramNames: [],
                factory: redirectTo('/taste-collection'),
            },
            {
                // Chat is an overlay, not a page: `/chat` (and its
                // `/chat/:sessionId` sibling) renders the feed underneath, then
                // opens the Chat Pane on the linked session or `?q=`/`?seed=` ingress.
                // Gated on the `amp` flag like `/feed` — with the gate off there's
                // no chat pane (or its DialogController bootstrap) to enter.
                pattern: '/chat',
                paramNames: [],
                factory: async () => {
                    if (!(await featureEnabled('amp'))) return NotFoundPage()
                    if (!getAuth().isSignedIn()) return SignInWall()
                    const { enterChatPane } = await pages.chatPaneEntry()
                    enterChatPane()
                    return renderFeed()
                },
            },
            {
                pattern: '/chat/:sessionId',
                paramNames: ['sessionId'],
                factory: async (params) => {
                    if (!(await featureEnabled('amp'))) return NotFoundPage()
                    if (!getAuth().isSignedIn()) return SignInWall()
                    const { enterChatPane } = await pages.chatPaneEntry()
                    enterChatPane({ sessionId: params.sessionId })
                    return renderFeed()
                },
            },
            {
                pattern: BROWSE_ALL_ROUTE,
                paramNames: [],
                allowPreload: true,
                factory: () => renderBrowseHome(),
            },
            { pattern: '/videos', paramNames: [], factory: redirectTo('/videos/page') },
            {
                pattern: '/videos/page',
                paramNames: [],
                allowPreload: true,
                factory: videoOnDemandGuarded(
                    kidsModeGuarded(async () => {
                        const { VideosPage } = await pages.VideosPage()
                        return VideosPage({ deps })
                    })
                ),
            },
            videoSeeMoreRoute('/videos/page/:id'),
            videoSeeMoreRoute('/browse/videos/home/recommendation/:id'),
            {
                pattern: '/video-podcasts',
                paramNames: [],
                allowPreload: true,
                factory: kidsModeGuarded(async () => {
                    const { VideoPodcastsPage } = await pages.VideoPodcastsPage()
                    return VideoPodcastsPage({ deps })
                }),
            },
            {
                // Starts playback, so never preloaded; the link resolves to null once home replaces it.
                pattern: '/videos/:id',
                paramNames: ['id'],
                factory: videoOnDemandGuarded(async (params: { id: string }) => {
                    if (!getAuth().isSignedIn()) return SignInWall()
                    const { playSharedVideo } = await pages.sharedVideo()
                    return (await playSharedVideo(deps, params.id)) ? null : WithheldRoutePage()
                }),
            },
            {
                pattern: '/profiles',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { ProfilesPage } = await pages.ProfilesPage()
                    return ProfilesPage()
                },
            },
            {
                pattern: '/identity/who-is-listening',
                paramNames: [],
                queryKeys: ['returnTo'],
                allowPreload: true,
                factory: async (_params, query) => {
                    if (!getAuth().isSignedIn()) return SignInWall()
                    const { SelectProfilePage } = await pages.SelectProfilePage()
                    return SelectProfilePage({ returnTo: query.get('returnTo') ?? undefined })
                },
            },
            {
                pattern: PROFILE_SELECTOR_ROUTE,
                paramNames: [],
                queryKeys: ['returnTo'],
                allowPreload: true,
                factory: async (_params, query) => {
                    if (!getAuth().isSignedIn()) return SignInWall()
                    const { SuggestedProfilesPage } = await pages.SuggestedProfilesPage()
                    return SuggestedProfilesPage({ returnTo: query.get('returnTo') ?? undefined })
                },
            },
            {
                pattern: PROFILE_SETUP_ROUTE,
                paramNames: [],
                queryKeys: ['id'],
                allowPreload: true,
                factory: async (_params, query) => {
                    if (!getAuth().isSignedIn()) return SignInWall()
                    const { ProfileSetupPage } = await pages.ProfileSetupPage()
                    return ProfileSetupPage({ id: query.get('id') ?? undefined })
                },
            },
            ...SUBSCRIPTION_MANAGEMENT_ROUTES.map((pattern) => ({
                pattern,
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    if (!getAuth().isSignedIn()) return WithheldRoutePage()
                    const { loadSubscriptionManagementAccess } = await pages.canChangeSubscription()
                    const access = await loadSubscriptionManagementAccess()
                    if (access === 'notFound') return WithheldRoutePage()
                    if (access === 'kidsRestricted') return renderKidsModeRestrictedPage()
                    const { SubscriptionPage } = await pages.SubscriptionPage()
                    return SubscriptionPage()
                },
            })),
            {
                pattern: '/preferences',
                paramNames: [],
                allowPreload: true,
                factory: kidsModeGuarded(async () => {
                    return (await pages.SettingsPage()).SettingsPage({
                        streamingSettings: deps.streamingSettings,
                        playerPromise: deps.playerPromise,
                    })
                }),
            },
            ...['/manage-settings', '/streamingQualitySettings', '/displayPreferences'].map(
                (pattern) => ({
                    pattern,
                    paramNames: [],
                    factory: redirectTo('/preferences'),
                })
            ),
            {
                pattern: '/help',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    return (await pages.HelpPage()).HelpPage()
                },
            },
            {
                // Linked from the avatar dropdown only when the debug flag is
                // set, but always registered so a direct URL works in prod.
                pattern: '/debug',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    return (await pages.DebugControlsPage()).DebugControlsPage()
                },
            },
            {
                pattern: '/podcasts',
                paramNames: [],
                allowPreload: true,
                factory: podcastsGuarded(
                    kidsModeGuarded(async () => {
                        const { Podcasts } = await pages.Podcasts()
                        return Podcasts({ deps })
                    })
                ),
            },
            {
                // Top-charts grid: uses `topPodcastShows` directly, not a
                // recommender, so it's its own page.
                pattern: '/podcasts/top-charts',
                paramNames: [],
                allowPreload: true,
                factory: podcastsGuarded(
                    kidsModeGuarded(async () => {
                        const { TopPodcastsPage } = await pages.TopPodcastsPage()
                        return TopPodcastsPage({ deps })
                    })
                ),
            },
            {
                pattern: '/library',
                paramNames: [],
                factory: redirectTo('/my/library'),
            },
            {
                // No `queryKeys`: `Search` owns `?q=` and `?type=` in place, so typing
                // never remounts the page.
                pattern: '/search',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { Search } = await pages.Search()
                    return Search({ controller: getSearchController(), deps })
                },
            },
            {
                pattern: '/search/:searchTerm',
                paramNames: ['searchTerm'],
                queryKeys: ['type'],
                factory: (params, query) => {
                    const term = params.searchTerm.trim()
                    const search = new URLSearchParams()
                    if (term) search.set('q', term)
                    const type = query.get('type')
                    if (term && type) search.set('type', type)
                    const suffix = search.size ? `?${search}` : ''
                    return redirectTo(`/search${suffix}`)()
                },
            },
            {
                pattern: '/search/:searchTerm/:resultType',
                paramNames: ['searchTerm', 'resultType'],
                queryKeys: ['type'],
                factory: async (params, query) => {
                    const { SEE_MORE_TYPE_MAP } = await pages.searchService()
                    const term = params.searchTerm.trim()
                    const search = new URLSearchParams()
                    if (term) search.set('q', term)
                    if (!term || !Object.hasOwn(SEE_MORE_TYPE_MAP, params.resultType)) {
                        return redirectTo(`/search${search.size ? `?${search}` : ''}`)()
                    }
                    if (query.get('type') === LIBRARY_FILTER_TYPE) {
                        search.set('type', LIBRARY_FILTER_TYPE)
                    }
                    return redirectTo(
                        `/find/see-more/${encodeURIComponent(params.resultType)}?${search}`
                    )()
                },
            },
            {
                // Per-entity see-more grid. Query rides `?q=` (link-shareable,
                // like `/search`); the entity slug sits in the path.
                pattern: '/find/see-more/:type',
                paramNames: ['type'],
                queryKeys: ['q', 'type'],
                allowPreload: true,
                factory: async (params, query) => {
                    if (params.type === 'audiobooks' && !(await audiobooksMarketEnabled()))
                        return WithheldRoutePage({ routePageAsSubType: true })
                    if (params.type === 'videos' && !(await videoOnDemandEnabled()))
                        return WithheldRoutePage({ routePageAsSubType: true })
                    const { SearchSeeMorePage } = await pages.SearchSeeMorePage()
                    return SearchSeeMorePage({
                        type: params.type,
                        query: query.get('q') ?? '',
                        isLibraryScope: query.get('type') === LIBRARY_FILTER_TYPE,
                        deps,
                    })
                },
            },
            {
                pattern: '/find/groupings/:id',
                paramNames: ['id'],
                queryKeys: ['title'],
                factory: async (params, query) => {
                    const { FindGroupingPage } = await pages.FindGroupingPage()
                    return FindGroupingPage({
                        id: params.id,
                        title: query.get('title') ?? '',
                    })
                },
            },
            {
                pattern: '/albums/:id',
                paramNames: ['id'],
                allowPreload: true,
                factory: async (params) => {
                    const { AlbumDetailPage } = await pages.AlbumDetailPage()
                    return AlbumDetailPage({ id: params.id, deps })
                },
            },
            {
                pattern: '/tracks/:id',
                paramNames: ['id'],
                allowPreload: true,
                factory: async (params) => {
                    const { TrackDetailPage } = await pages.TrackDetailPage()
                    return TrackDetailPage({ id: params.id, deps })
                },
            },
            {
                pattern: '/tracks/:id/related-artists',
                paramNames: ['id'],
                allowPreload: true,
                factory: async (params) => {
                    const { TrackRelatedArtistsPage } = await pages.trackSeeAll()
                    return TrackRelatedArtistsPage({ id: params.id, deps })
                },
            },
            {
                pattern: '/tracks/:id/related-playlists',
                paramNames: ['id'],
                allowPreload: true,
                factory: async (params) => {
                    const { TrackRelatedPlaylistsPage } = await pages.trackSeeAll()
                    return TrackRelatedPlaylistsPage({ id: params.id, deps })
                },
            },
            {
                pattern: '/tracks/:id/related',
                paramNames: ['id'],
                allowPreload: true,
                factory: async (params) => {
                    const { TrackRelatedTracksPage } = await pages.trackSeeAll()
                    return TrackRelatedTracksPage({ id: params.id, deps })
                },
            },
            {
                pattern: '/browse/music-items/artist/:id/related-artists',
                paramNames: ['id'],
                allowPreload: true,
                factory: async (params) => {
                    const { ArtistRelatedArtistsPage } = await pages.ArtistRelatedArtistsPage()
                    return ArtistRelatedArtistsPage({ id: params.id, deps })
                },
            },
            {
                pattern: '/browse/tracks/artist/:id/all-access-tracks',
                paramNames: ['id'],
                allowPreload: true,
                factory: async (params) => {
                    const { ArtistAllAccessSongsPage } = await pages.ArtistAllAccessSongsPage()
                    return ArtistAllAccessSongsPage({ id: params.id, deps })
                },
            },
            // Dedicated artist see-all routes: each has its own fetch shape, so they
            // can't collapse into the generic SeeAllPage.
            {
                pattern: '/browse/music-items/artist/:id/chronological-albums',
                paramNames: ['id'],
                allowPreload: true,
                factory: async (params) => {
                    const { ArtistChronologicalAlbumsPage } =
                        await pages.ArtistChronologicalAlbumsPage()
                    return ArtistChronologicalAlbumsPage({ id: params.id, deps })
                },
            },
            {
                pattern: '/browse/music-items/artist/:id/popular-albums',
                paramNames: ['id'],
                allowPreload: true,
                factory: async (params) => {
                    const { ArtistPopularAlbumsPage } = await pages.ArtistPopularAlbumsPage()
                    return ArtistPopularAlbumsPage({ id: params.id, deps })
                },
            },
            {
                pattern: '/browse/tracks/artist/:id/popular-songs',
                paramNames: ['id'],
                allowPreload: true,
                factory: async (params) => {
                    const { ArtistPopularSongsPage } = await pages.ArtistPopularSongsPage()
                    return ArtistPopularSongsPage({ id: params.id, deps })
                },
            },
            {
                pattern: '/browse/videos/artist/:id/popular-videos',
                paramNames: ['id'],
                allowPreload: true,
                factory: async (params) => {
                    const { ArtistPopularVideosPage } = await pages.ArtistPopularVideosPage()
                    return ArtistPopularVideosPage({ id: params.id, deps })
                },
            },
            {
                pattern: '/browse/music-items/artist/:id/related-stations',
                paramNames: ['id'],
                allowPreload: true,
                factory: async (params) => {
                    const { ArtistRelatedStationsPage } = await pages.ArtistRelatedStationsPage()
                    return ArtistRelatedStationsPage({ id: params.id, deps })
                },
            },
            {
                pattern: '/artists/:id/related-podcasts',
                paramNames: ['id'],
                allowPreload: true,
                factory: podcastsGuarded(
                    kidsModeGuarded(async (params) => {
                        const { ArtistRelatedPodcastsPage } =
                            await pages.ArtistRelatedPodcastsPage()
                        return ArtistRelatedPodcastsPage({ id: params.id, deps })
                    })
                ),
            },
            {
                // GH's legacy alias: other clients mint the long form.
                pattern: '/artists/:id/related-podcasts-to-artist',
                paramNames: ['id'],
                factory: (params) => {
                    getRouter().navigateTo(
                        `/artists/${encodeURIComponent(params.id)}/related-podcasts`,
                        { replace: true }
                    )
                    return null
                },
            },
            {
                pattern: '/browse/music-items/artist/:id/related-playlists',
                paramNames: ['id'],
                allowPreload: true,
                factory: async (params) => {
                    const { ArtistRelatedPlaylistsPage } = await pages.ArtistRelatedPlaylistsPage()
                    return ArtistRelatedPlaylistsPage({ id: params.id, deps })
                },
            },
            {
                // Registered BEFORE `/artists/:id` so the `polls` segment
                // isn't shadowed. `pollId` is ignored, as in GH.
                pattern: '/artists/:id/polls/:pollId',
                paramNames: ['id', 'pollId'],
                allowPreload: true,
                factory: async (params) => {
                    const { ArtistFanPollPage } = await pages.ArtistFanPollPage()
                    return ArtistFanPollPage({ id: params.id })
                },
            },
            {
                // `:slug?` is SEO-only: the server emits a slugged canonical
                // `/artists/:id/:slug`, so a cold/bookmarked/shared load must
                // resolve here. Registered AFTER the specific
                // `/artists/:id/<segment>` routes above so ordering (=
                // specificity) lets those match first — the id is what resolves.
                pattern: '/artists/:id/:slug?',
                paramNames: ['id', 'slug'],
                queryKeys: ['promotionSlot'],
                allowPreload: true,
                factory: async (params, query) => {
                    const { ArtistDetailPage } = await pages.ArtistDetailPage()
                    const promotionSlot = query.get('promotionSlot') ?? undefined
                    return ArtistDetailPage({ id: params.id, promotionSlot, deps })
                },
            },
            {
                // Category landing (`/podcasts/categories/<title>/dmp.category.<uuid>`,
                // matches green hornet's `[categoryTitle]/[categoryId]`).
                // Registered BEFORE `/podcasts/:categoryId/:strategyId` so its
                // two-segment regex doesn't capture `categories` + `<title>`.
                // `:categoryId?` stays optional for pre-existing name-only
                // bookmarks/links — the page falls back to a name lookup.
                pattern: '/podcasts/categories/:categoryTitle/:categoryId?',
                paramNames: ['categoryTitle', 'categoryId'],
                allowPreload: true,
                factory: podcastsGuarded(
                    kidsModeRestrictedPageGuarded(async (params) => {
                        const { PodcastCategoryPage } = await pages.PodcastCategoryPage()
                        return PodcastCategoryPage({
                            categoryTitle: params.categoryTitle,
                            categoryId: params.categoryId,
                            deps,
                        })
                    })
                ),
            },
            {
                // `showId` is constrained to a UUID so a slugged URL resolves
                // here without colliding with the sibling shapes
                // (`providers/:id`, `:categoryId/:strategyId`). `:slug?` is SEO-only.
                pattern: '/podcasts/:showId(UUID)/:slug?',
                paramNames: ['showId', 'slug'],
                allowPreload: true,
                factory: podcastsGuarded(
                    kidsModeGuarded(async (params) => {
                        const { PodcastShowDetailPage } = await pages.PodcastShowDetailPage()
                        return PodcastShowDetailPage({ id: params.showId, deps })
                    })
                ),
            },
            {
                // Podcast episode deep link. `:slug?` is SEO-only; both ids are
                // used — episode id fetches the payload, show id is required by
                // the `episode` recommender (targets [episodeId, showId]).
                pattern: '/podcasts/:showId/episodes/:episodeId/:slug?',
                paramNames: ['showId', 'episodeId', 'slug'],
                allowPreload: true,
                factory: podcastsGuarded(
                    kidsModeGuarded(async (params) => {
                        const { PodcastEpisodePage } = await pages.PodcastEpisodePage()
                        return PodcastEpisodePage({
                            id: params.episodeId,
                            showId: params.showId,
                            deps,
                        })
                    })
                ),
            },
            {
                // Provider-scoped podcast landing. Registered BEFORE
                // `/podcasts/:categoryId/:strategyId` so `providers` isn't shadowed.
                pattern: '/podcasts/providers/:providerId',
                paramNames: ['providerId'],
                allowPreload: true,
                factory: podcastsGuarded(
                    kidsModeGuarded(async (params) => {
                        const { PodcastProviderPage } = await pages.PodcastProviderPage()
                        return PodcastProviderPage({
                            providerId: params.providerId,
                            deps,
                        })
                    })
                ),
            },
            {
                pattern: '/podcasts/pages/:target',
                paramNames: ['target'],
                allowPreload: true,
                factory: podcastsGuarded(
                    kidsModeGuarded(async (params) => {
                        const { PodcastPagesPage } = await pages.PodcastPagesPage()
                        return PodcastPagesPage({ target: params.target, deps })
                    })
                ),
            },
            // Before `/podcasts/:categoryId/:strategyId`, which would read `library` as a category.
            ...['latest', 'playlist', 'shows'].map((tab) => ({
                pattern: `/podcasts/library/${tab}`,
                paramNames: [],
                factory: kidsModeGuarded(redirectTo(`/my/podcasts/${tab}`)),
            })),
            {
                pattern: '/podcasts/:categoryId/:strategyId',
                paramNames: ['categoryId', 'strategyId'],
                allowPreload: true,
                factory: podcastsGuarded(
                    kidsModeGuarded(async (params) => {
                        // Two shapes share this route: a category see-more carries a
                        // `dmp.category.<uuid>` first segment (curated-content query);
                        // a podcasts-home "See more" carries a strategy name (generic
                        // recommender). Route by whether the first segment embeds a UUID.
                        if (isPodcastCategorySegment(params.categoryId)) {
                            const { PodcastSeeMorePage } = await pages.PodcastSeeMorePage()
                            return PodcastSeeMorePage({
                                categoryId: params.categoryId,
                                strategyId: params.strategyId,
                                deps,
                            })
                        }
                        const { SeeAllPage } = await pages.SeeAllPage()
                        return SeeAllPage({ widgetId: params.strategyId, deps })
                    })
                ),
            },
            {
                pattern: '/new',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { NewReleasesPage } = await pages.NewReleasesPage()
                    return NewReleasesPage({ deps })
                },
            },
            // /new/* see-all pages — flat see-all grids backed by fixed
            // `uri:///new/...` recommenders.
            {
                pattern: '/new/songs',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { FixedTitleSeeAllPage } = await pages.fixedTitleSeeAll()
                    return FixedTitleSeeAllPage({ recommenderId: 'uri:///new/songs', deps })
                },
            },
            {
                pattern: '/new/albums',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { FixedTitleSeeAllPage } = await pages.fixedTitleSeeAll()
                    return FixedTitleSeeAllPage({ recommenderId: 'uri:///new/albums', deps })
                },
            },
            {
                pattern: '/new/playlists',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { FixedTitleSeeAllPage } = await pages.fixedTitleSeeAll()
                    return FixedTitleSeeAllPage({ recommenderId: 'uri:///new/playlists', deps })
                },
            },
            {
                pattern: '/new/new-releases-for-you',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { FixedTitleSeeAllPage } = await pages.fixedTitleSeeAll()
                    return FixedTitleSeeAllPage({
                        recommenderId: 'uri:///new/new-releases-for-you',
                        deps,
                    })
                },
            },
            {
                pattern: '/new/upcoming-releases',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { FixedTitleSeeAllPage } = await pages.fixedTitleSeeAll()
                    return FixedTitleSeeAllPage({
                        recommenderId: 'uri:///new/upcoming-releases',
                        deps,
                    })
                },
            },
            {
                pattern: '/my',
                paramNames: [],
                factory: redirectTo('/my/library'),
            },
            {
                pattern: '/my/library',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { Library } = await pages.Library()
                    return Library({ deps })
                },
            },
            ...['/my/playlists', '/my/playlists/all'].map((pattern) => ({
                pattern,
                paramNames: [],
                factory: redirectTo('/my/playlists/by-me'),
            })),
            {
                pattern: '/my/playlists/by-me',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { MyPlaylistsPage } = await pages.MyPlaylistsPage()
                    return MyPlaylistsPage({ deps, variant: 'by-me' })
                },
            },
            {
                pattern: '/my/playlists/followed',
                paramNames: [],
                factory: async () => {
                    const { MyPlaylistsPage } = await pages.MyPlaylistsPage()
                    return MyPlaylistsPage({ deps, variant: 'followed' })
                },
            },
            {
                pattern: '/my/downloads',
                paramNames: [],
                factory: downloadsPage(deps, pages, () => ({ tab: 'all' })),
            },
            {
                pattern: '/my/downloads/albums',
                paramNames: [],
                factory: downloadsPage(deps, pages, () => ({ tab: 'albums' })),
            },
            {
                pattern: '/my/downloads/playlists',
                paramNames: [],
                factory: downloadsPage(deps, pages, () => ({ tab: 'playlists' })),
            },
            {
                pattern: '/my/downloads/albums/:id',
                paramNames: ['id'],
                factory: downloadsPage(deps, pages, (id) => ({ kind: 'album', id })),
            },
            {
                pattern: '/my/downloads/playlists/:id',
                paramNames: ['id'],
                factory: downloadsPage(deps, pages, (id) => ({ kind: 'playlist', id })),
            },
            {
                pattern: '/my/computer',
                paramNames: [],
                factory: computerPage(deps, pages, () => ({ tab: 'songs' })),
            },
            {
                pattern: '/my/computer/albums',
                paramNames: [],
                factory: computerPage(deps, pages, () => ({ tab: 'albums' })),
            },
            {
                pattern: '/my/computer/artists',
                paramNames: [],
                factory: computerPage(deps, pages, () => ({ tab: 'artists' })),
            },
            {
                pattern: '/my/computer/playlists',
                paramNames: [],
                factory: computerPage(deps, pages, () => ({ tab: 'playlists' })),
            },
            {
                pattern: '/my/computer/playlists/:id',
                paramNames: ['id'],
                factory: computerPage(deps, pages, (id) => ({ kind: 'playlist', id })),
            },
            {
                pattern: '/my/computer/albums/:id',
                paramNames: ['id'],
                factory: computerPage(deps, pages, (id) => ({ kind: 'album', id })),
            },
            {
                pattern: '/my/computer/artists/:id',
                paramNames: ['id'],
                factory: computerPage(deps, pages, (id) => ({ kind: 'artist', id })),
            },
            {
                pattern: '/my/albums',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { MyLibraryListPage } = await pages.MyLibraryListPage()
                    return MyLibraryListPage({ deps, kind: 'albums' })
                },
            },
            {
                pattern: '/my/artists',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { MyLibraryListPage } = await pages.MyLibraryListPage()
                    return MyLibraryListPage({ deps, kind: 'artists' })
                },
            },
            {
                pattern: '/my/songs',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { MyLibraryListPage } = await pages.MyLibraryListPage()
                    return MyLibraryListPage({ deps, kind: 'songs' })
                },
            },
            {
                pattern: '/my/podcasts/playlist',
                paramNames: [],
                allowPreload: true,
                factory: podcastsGuarded(
                    kidsModeRestrictedPageGuarded(async () => {
                        const { MyPodcastPlaylistPage } = await pages.MyPodcastPlaylistPage()
                        return MyPodcastPlaylistPage({ deps })
                    })
                ),
            },
            {
                // Newest episodes across followed shows — the default podcasts tab.
                pattern: '/my/podcasts/latest',
                paramNames: [],
                allowPreload: true,
                factory: podcastsGuarded(
                    kidsModeRestrictedPageGuarded(async () => {
                        const { LatestPodcastsPage } = await pages.LatestPodcastsPage()
                        return LatestPodcastsPage({ deps })
                    })
                ),
            },
            {
                // Followed shows grid (`followedPodcastShows` as a card grid).
                pattern: '/my/podcasts/shows',
                paramNames: [],
                allowPreload: true,
                factory: podcastsGuarded(
                    kidsModeRestrictedPageGuarded(async () => {
                        const { MyLibraryListPage } = await pages.MyLibraryListPage()
                        return MyLibraryListPage({ deps, kind: 'podcasts' })
                    })
                ),
            },
            {
                pattern: '/my/podcasts',
                paramNames: [],
                factory: kidsModeGuarded(redirectTo('/my/podcasts/latest')),
            },
            {
                pattern: '/my/purchases',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    if (!isClassicTerritory((await getMarketplace()).musicTerritory))
                        return NotFoundPage()
                    const { MyLibraryListPage } = await pages.MyLibraryListPage()
                    return MyLibraryListPage({ deps, kind: 'purchased' })
                },
            },
            {
                // Continue Listening, Saved Audiobooks, and Previous borrows.
                pattern: '/my/audiobooks',
                paramNames: [],
                allowPreload: true,
                factory: kidsModeGuarded(
                    audiobooksGuarded(
                        async () => {
                            const { MyAudiobooksPage } = await pages.MyAudiobooksPage()
                            return MyAudiobooksPage({ deps })
                        },
                        { routePageAsSubType: true }
                    )
                ),
            },
            {
                pattern: '/music',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    return (await pages.MusicHome()).MusicHome({ deps })
                },
            },
            {
                pattern: '/browse/music-items/home/recommendation/:widgetId',
                paramNames: ['widgetId'],
                allowPreload: true,
                factory: async (params) => {
                    const { SeeAllPage } = await pages.SeeAllPage()
                    return SeeAllPage({ widgetId: params.widgetId, deps })
                },
            },
            browseHomeSeeMoreRoute('/browse/albums/home/recommendation/:id'),
            browseHomeSeeMoreRoute('/browse/stations/home/recommendation/:id'),
            browseHomeSeeMoreRoute('/browse/tracks/home/recommendation/:id'),
            // Charts hub — stacks the four popular-chart shovelers, each linking
            // to its dedicated see-all page below.
            {
                pattern: '/charts',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { ChartsHubPage } = await pages.ChartsHubPage()
                    return ChartsHubPage({ deps })
                },
            },
            {
                // GH serves its charts hub at /popular; /charts stays canonical.
                pattern: '/popular',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { ChartsHubPage } = await pages.ChartsHubPage()
                    return ChartsHubPage({ deps })
                },
            },
            // /popular/* — flat see-all grids backed by fixed `uri:///popular/...`
            // recommenders, passed to SeeAllPage as recommenderId.
            {
                pattern: '/popular/songs',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { FixedTitleSeeAllPage } = await pages.fixedTitleSeeAll()
                    return FixedTitleSeeAllPage({ recommenderId: 'uri:///popular/top-songs', deps })
                },
            },
            {
                pattern: '/popular/albums',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { FixedTitleSeeAllPage } = await pages.fixedTitleSeeAll()
                    return FixedTitleSeeAllPage({
                        recommenderId: 'uri:///popular/top-albums',
                        deps,
                    })
                },
            },
            {
                pattern: '/popular/playlists',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { FixedTitleSeeAllPage } = await pages.fixedTitleSeeAll()
                    return FixedTitleSeeAllPage({
                        recommenderId: 'uri:///popular/top-playlists',
                        deps,
                    })
                },
            },
            {
                pattern: '/popular/trending-songs',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { FixedTitleSeeAllPage } = await pages.fixedTitleSeeAll()
                    return FixedTitleSeeAllPage({
                        recommenderId: 'uri:///popular/trending-songs',
                        deps,
                    })
                },
            },
            {
                // After every literal `/my/playlists/*` route, which this would otherwise shadow.
                pattern: '/my/playlists/:id',
                paramNames: ['id'],
                factory: (params) => {
                    getRouter().navigateTo(`/playlists/${encodeURIComponent(params.id)}`, {
                        replace: true,
                    })
                    return null
                },
            },
            {
                // `topStations` isn't a recommender, so it can't use SeeAllPage's
                // `recommender(id:)` wrapper — mirror it as a dedicated page.
                pattern: '/popular/stations',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { TopStationsPage } = await pages.TopStationsPage()
                    return TopStationsPage({ deps })
                },
            },
            {
                // Stations landing hub — stacks Stations for You, Recently Played,
                // and Top Stations, each linking to its see-all page.
                pattern: '/stations',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { StationsPage } = await pages.StationsPage()
                    return StationsPage({ deps })
                },
            },
            {
                // Top Stations see-all, reachable from the Stations hub's URL
                // space (mirrors `/popular/stations`).
                pattern: '/stations/top',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { TopStationsPage } = await pages.TopStationsPage()
                    return TopStationsPage({ deps })
                },
            },
            {
                // Stations for You see-all. No `stations-for-you` recommender
                // exists, so it reads from `user.topStations` like the hub row.
                pattern: '/recommended/stations',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { StationsForYouPage } = await pages.StationsForYouPage()
                    return StationsForYouPage({ deps })
                },
            },
            // Playlist see-all pages — same recommender shape as /popular/*.
            {
                pattern: '/playlists/playlists-for-you',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { FixedTitleSeeAllPage } = await pages.fixedTitleSeeAll()
                    return FixedTitleSeeAllPage({
                        recommenderId: 'uri:///playlists/playlists-for-you',
                        deps,
                    })
                },
            },
            {
                pattern: '/playlists/recently-updated-playlists',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { FixedTitleSeeAllPage } = await pages.fixedTitleSeeAll()
                    return FixedTitleSeeAllPage({
                        recommenderId: 'uri:///playlists/recently-updated-playlists',
                        deps,
                    })
                },
            },
            {
                pattern: '/recently/added',
                paramNames: [],
                factory: redirectTo('/my/songs'),
            },
            {
                pattern: '/recently/played/songs',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { SongHistoryPage } = await pages.SongHistoryPage()
                    return SongHistoryPage({ deps })
                },
            },
            {
                pattern: '/recently/played/stations',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { RecentlyPlayedStationsPage } = await pages.RecentlyPlayedStationsPage()
                    return RecentlyPlayedStationsPage({ deps })
                },
            },
            {
                pattern: '/recently/purchased',
                paramNames: [],
                factory: redirectTo('/my/purchases'),
            },
            {
                // Target `/my/library` directly: chaining through the `/library` alias would
                // cost a second redirect.
                pattern: '/recently/played',
                paramNames: [],
                factory: redirectTo('/my/library'),
            },
            // Parity stubs for entity types amu doesn't fully render yet, so
            // deep links don't 404 while the underlying pieces ship.
            {
                pattern: '/maestro',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { MaestroPage } = await pages.MaestroPage()
                    return MaestroPage({ deps })
                },
            },
            {
                pattern: '/live',
                paramNames: [],
                allowPreload: true,
                factory: kidsModeGuarded(async () => {
                    const { LiveEventsPage } = await pages.LiveEventsPage()
                    return LiveEventsPage()
                }),
            },
            {
                pattern: '/events/:id',
                paramNames: ['id'],
                allowPreload: true,
                factory: kidsModeGuarded(async (params) => {
                    const { LiveEventDetailPage } = await pages.LiveEventDetailPage()
                    return LiveEventDetailPage({ id: params.id, deps })
                }),
            },
            {
                pattern: '/live/events/:id',
                paramNames: ['id'],
                factory: (params) => {
                    getRouter().navigateTo(`/events/${encodeURIComponent(params.id)}`, {
                        replace: true,
                    })
                    return null
                },
            },
            {
                pattern: '/live/concerts/:id',
                paramNames: ['id'],
                factory: kidsModeGuarded(async (params) => {
                    getRouter().navigateTo(`/concerts/${encodeURIComponent(params.id)}`, {
                        replace: true,
                    })
                    return null
                }),
            },
            {
                // amu folds the lineup into event detail; redirect the GH
                // lineup shape so shared links resolve instead of 404ing.
                pattern: '/live/events/:eventId/lineup',
                paramNames: ['eventId'],
                factory: (params) => {
                    getRouter().navigateTo(`/events/${encodeURIComponent(params.eventId)}`, {
                        replace: true,
                    })
                    return null
                },
            },
            {
                // AMP live-event detail, reached from `LiveEventCard`'s fallback
                // CTA. Distinct from `/events/:id` so the AMP surface can iterate
                // without regressing the shared detail page.
                pattern: '/liveEvents/:id',
                paramNames: ['id'],
                allowPreload: true,
                factory: kidsModeGuarded(async (params) => {
                    const { LiveEventDetailPage } = await pages.AmpLiveEventDetailPage()
                    return LiveEventDetailPage({ id: params.id })
                }),
            },
            {
                // Nearby concerts hub — Firefly's `nearbyConcerts` (default
                // 100mi radius, upcoming-only).
                pattern: '/concerts',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { ConcertsPage } = await pages.ConcertsPage()
                    return ConcertsPage()
                },
            },
            {
                pattern: '/concerts/:id',
                paramNames: ['id'],
                allowPreload: true,
                factory: kidsModeGuarded(async (params) => {
                    const { ConcertDetailPage } = await pages.ConcertDetailPage()
                    return ConcertDetailPage({ id: params.id })
                }),
            },
            {
                pattern: '/merch',
                paramNames: [],
                allowPreload: true,
                factory: merchGuarded(async () => {
                    const { MerchShopPage } = await pages.MerchShopPage()
                    return MerchShopPage({ deps })
                }),
            },
            {
                // Category-scoped merch storefront: the query picks the page (shop,
                // category, see-all), so a tile linking to another `?tag=` opens it.
                pattern: '/merch/shop',
                paramNames: [],
                queryKeys: ['tag', 'category', 'sequence', 'strategyId'],
                allowPreload: true,
                factory: merchGuarded(async (_params, query) => {
                    const { MerchShopPage } = await pages.MerchShopPage()
                    const categoryIds = [
                        query.get('tag'),
                        query.get('category'),
                        query.get('sequence'),
                    ].filter((v): v is string => !!v)
                    const strategyId = query.get('strategyId')
                    return MerchShopPage({
                        deps,
                        ...(categoryIds.length > 0 ? { categoryIds } : {}),
                        ...(strategyId ? { strategyId } : {}),
                    })
                }),
            },
            {
                // `:artistName?` is SEO-only; `?category=a,b` preselects product-type
                // filters, which the page then rewrites in place as they change.
                pattern: '/merch/artists/:id/:artistName?',
                paramNames: ['id', 'artistName'],
                allowPreload: true,
                factory: merchGuarded(async (params, query) => {
                    const { ArtistMerchPage } = await pages.ArtistMerchPage()
                    const category = query.get('category')
                    return ArtistMerchPage({
                        id: params.id,
                        deps,
                        initialFilters: category ? category.split(',') : [],
                    })
                }),
            },
            {
                pattern: '/merch/items/:id',
                paramNames: ['id'],
                allowPreload: true,
                factory: merchGuarded(async (params) => {
                    const { MerchDetailPage } = await pages.MerchDetailPage()
                    return MerchDetailPage({ id: params.id })
                }),
            },
            {
                // The artist scope drives the detail page's artist-information block.
                pattern: '/merch/artists/:artistId/items/:id',
                paramNames: ['artistId', 'id'],
                allowPreload: true,
                factory: merchGuarded(async (params) => {
                    const { MerchDetailPage } = await pages.MerchDetailPage()
                    return MerchDetailPage({
                        id: params.id,
                        artistId: params.artistId,
                    })
                }),
            },
            {
                pattern: '/genres/:id',
                paramNames: ['id'],
                allowPreload: true,
                factory: async (params) => {
                    const { GenreDetailPage } = await pages.GenreDetailPage()
                    return GenreDetailPage({ id: params.id, deps })
                },
            },
            {
                pattern: '/curated/:id',
                paramNames: ['id'],
                allowPreload: true,
                factory: async (params) => {
                    const { CuratedTopicPage } = await pages.CuratedTopicPage()
                    return CuratedTopicPage({ id: params.id, deps })
                },
            },
            {
                // See-all for one of a genre page's shovelers. Green hornet
                // targets the recommender via `uri:///category/<id>/<widgetId>`.
                pattern: '/genres/:id/:recommenderId',
                paramNames: ['id', 'recommenderId'],
                allowPreload: true,
                factory: async (params) => {
                    const { SeeAllPage } = await pages.SeeAllPage()
                    return SeeAllPage({
                        recommenderId: `uri:///category/${params.id}/${params.recommenderId}`,
                        deps,
                    })
                },
            },
            {
                pattern: '/audiobooks/library',
                paramNames: [],
                factory: kidsModeGuarded(redirectTo('/my/audiobooks')),
            },
            {
                // Curated audiobook browse home. Audiobook rows route to
                // `/audiobooks/:id`; category tiles to `/audiobooks/categories/:id`.
                pattern: '/audiobooks',
                paramNames: [],
                allowPreload: true,
                factory: kidsModeGuarded(
                    audiobooksGuarded(
                        async () => {
                            const { AudiobooksHomePage } = await pages.AudiobooksHomePage()
                            return AudiobooksHomePage({ deps })
                        },
                        { routePageAsSubType: true }
                    )
                ),
            },
            {
                // Audiobook category browse — categoryId is the
                // recommendation-collection id; the page renders it as a
                // BrowseHomePage stack headed by the category's real name.
                pattern: '/audiobooks/categories/:categoryId',
                paramNames: ['categoryId'],
                allowPreload: true,
                factory: kidsModeGuarded(
                    audiobooksGuarded(async (params) => {
                        const { AudiobookCategoryPage } = await pages.AudiobookCategoryPage()
                        return AudiobookCategoryPage({ id: params.categoryId, deps })
                    })
                ),
            },
            {
                // Owned/in-library titles get an in-app Play via the Audible
                // sub-player, so the page needs playback deps.
                pattern: '/audiobooks/:audiobookId',
                paramNames: ['audiobookId'],
                allowPreload: true,
                factory: kidsModeGuarded(
                    audiobooksGuarded(
                        async (props) => {
                            const { AudiobookDetailPage } = await pages.AudiobookDetailPage()
                            return AudiobookDetailPage({ id: props.audiobookId, deps })
                        },
                        { routePageAsSubType: true }
                    )
                ),
            },
            {
                pattern: '/now-playing',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const [{ NowPlayingPage }, sidePanel, podcastTransport] = await Promise.all([
                        pages.NowPlayingPage(),
                        isTv() ? tvNowPlayingSidePanel(deps, pages) : undefined,
                        podcastStageTransport(deps, pages),
                    ])
                    return NowPlayingPage({
                        deps,
                        podcastTransport,
                        ...(sidePanel ? { sidePanel } : null),
                    })
                },
            },
            {
                // The invite resolves from the token alone; the playlist id segment is not read.
                pattern: '/user-playlists/:playlistId/invitations/:token',
                paramNames: ['playlistId', 'token'],
                allowPreload: true,
                factory: async (params) => {
                    const { PlaylistInvitePage } = await pages.PlaylistInvitePage()
                    return PlaylistInvitePage({ inviteId: params.token })
                },
            },
            {
                // Viewer's own activity feed — auth-gated since it reads
                // `user.activity`, so anonymous sessions hit the sign-in wall.
                pattern: ACTIVITY_FEED_ROUTE,
                paramNames: [],
                allowPreload: true,
                factory: kidsModeRestrictedPageGuarded(async () => {
                    if (!getAuth().isSignedIn()) return SignInWall()
                    if (!(await loadIsActivityFeedAvailable())) {
                        return FeatureUnavailablePage('activityFeedUnavailableInTerritory')
                    }
                    const { ActivityFeedPage } = await pages.ActivityFeedPage()
                    return ActivityFeedPage()
                }),
            },
            {
                pattern: '/profiles/:userId/public-playlists',
                paramNames: ['userId'],
                allowPreload: true,
                factory: kidsModeRestrictedPageGuarded(async (params) => {
                    const { PublicPlaylistsPage } = await pages.PublicPlaylistsPage()
                    return PublicPlaylistsPage({ userId: params.userId, deps })
                }),
            },
            {
                pattern: '/profiles/:userId/followers',
                paramNames: ['userId'],
                allowPreload: true,
                factory: kidsModeRestrictedPageGuarded(async (params) => {
                    const { UserFollowsPage } = await pages.UserFollowsPage()
                    return UserFollowsPage({ userId: params.userId, variant: 'followers' })
                }),
            },
            {
                pattern: '/profiles/:userId/following',
                paramNames: ['userId'],
                allowPreload: true,
                factory: kidsModeRestrictedPageGuarded(async (params) => {
                    const { UserFollowsPage } = await pages.UserFollowsPage()
                    return UserFollowsPage({ userId: params.userId, variant: 'following' })
                }),
            },
            {
                // Registered AFTER the `/profiles/:userId/{public-playlists,followers,
                // following}` sub-routes so its regex doesn't shadow them (the
                // router walks routes in registration order).
                pattern: '/profiles/:userId',
                paramNames: ['userId'],
                allowPreload: true,
                factory: kidsModeGuarded(async (params) => {
                    const { ProfileDetailPage } = await pages.ProfileDetailPage()
                    return ProfileDetailPage({ userId: params.userId })
                }),
            },
            {
                pattern: '/playlists',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { PlaylistsHubPage } = await pages.PlaylistsHubPage()
                    return PlaylistsHubPage({ deps })
                },
            },
            {
                // The slug maps to an opaque Firefly playlist-set enum that
                // `/playlists/:id` can't resolve, so resolve it to the viewer's
                // real id and redirect. Registered BEFORE `/playlists/:playlistId`
                // so `pp` isn't captured as an id. Auth-gated (viewer's own).
                pattern: '/playlists/pp/:slug',
                paramNames: ['slug'],
                factory: async (params) => {
                    if (!getAuth().isSignedIn()) return SignInWall()
                    const { resolvePersonalizedPlaylistId } =
                        await pages.personalizedPlaylistResolver()
                    const playlistId = await resolvePersonalizedPlaylistId(params.slug)
                    if (!playlistId) return NotFoundPage()
                    getRouter().navigateTo(`/playlists/${encodeURIComponent(playlistId)}`, {
                        replace: true,
                    })
                    return null
                },
            },
            // Maestro's just-composed generative playlist — not yet a persisted
            // playlist, so it renders from the in-memory bag rather than
            // `playlist(id:)`, with a save-to-public control. Registered BEFORE
            // the generic aliases so `llm-generated` isn't captured as an id.
            generatedPlaylistRoute('/playlists/llm-generated/:token'),
            generatedPlaylistRoute('/user-playlists/llm-generated/:token'),
            playlistDetailRoute('/playlists/:playlistId/:mode?'),
            // Alias for the "Listen on Amazon Music" share URL Hornet emits.
            playlistDetailRoute('/user-playlists/:playlistId/:mode?'),
            {
                // Voice / deep-link entry (`amp:///play?q=`, iOS Siri "Play X on
                // Amu"). Fires search-and-play, then redirects home so the URL
                // bar doesn't rest on a transient action path.
                pattern: PLAY_ROUTE,
                paramNames: [],
                queryKeys: ['q'],
                factory: async (_params, query) => {
                    const q = query.get('q') ?? ''
                    if (q) {
                        const { playSearch } = await pages.playSearch()
                        playSearch(q, {
                            cloudQueueController: deps.cloudQueueController,
                            playlistController: deps.playlistController,
                        }).catch(() => {})
                    }
                    const { Feed } = await pages.Feed()
                    // Raw `replaceState`, deliberately NOT routed through the
                    // router: here the URL target (`/`) differs from what we
                    // render (`Feed`), and routing would fire the `/` redirect
                    // chain and swap the surface. `/play` is an external-only,
                    // transient depth-0 entry, so the `currentPath` desync is benign.
                    history.replaceState(null, '', HOME_ROUTE)
                    return Feed({ deps })
                },
            },
        ],
        fallback: NotFoundPage,
        errorFallback: ErrorPage,
        pendingView: () => DetailPageSkeleton(),
    })

    return () => {
        stopPageViews()
        router.clearRoutes()
    }
}

/**
 * Reloads the view route when the auth status changes to/from signed in <--> signed out
 */
export function initializeRouter(): Unsubscribe {
    const { add, dispose } = createDisposer()
    const router = getRouter()
    const auth = getAuth()

    // Feed the agent view-context provider's per-route baseline from the router
    // (one-way router → viewContext injection; the provider never imports here).
    add(startViewContextRouterFeed())
    // Feed the same provider's now-playing slice from the shared player so the
    // agent knows the current track on any route (blackbolt → viewContext).
    add(startNowPlayingViewContextFeed())

    // Raise the chat pane on real navigation to /feed — on `navigate`, not the
    // route factory, so a hover/idle preload build never opens it and a re-nav
    // to the memoized /feed still does. Gate + sign-in mirror the factory so a
    // gated-off / anon session (NotFound / SignInWall) doesn't raise chat.
    add(
        router.on('navigate', ({ current }) => {
            if (current !== '/feed') return
            featureEnabled('amp')
                .then((enabled) => {
                    if (enabled && auth.isSignedIn()) openChatPane()
                })
                .catch(noop)
        })
    )

    router.on('navigating', (event) => {
        // Do not show pages when not logged in on TV.
        if (isTv() && !auth.isSignedIn()) {
            event.preventDefault()
        }

        // Block navigation while signing in; a reload fires when it completes.
        if (auth.status.value === AuthStatus.PENDING) {
            event.preventDefault()
        }
    })

    add(
        auth.status.onData((value, previous) => {
            if (previous && value !== AuthStatus.PENDING) {
                router.hardReload()
            }
        })
    )

    add(bindFullscreenEscapeLock())
    add(
        installLazily(
            import('@/device/input/backKey'),
            (m) => m.bindBackKey(getDeviceConfig().keyMap, back),
            errorHandler
        )
    )

    return dispose
}
